import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  BookingService,
  FirebaseAuthenticationService,
} from '@app/core/services';
import { BookingRepository } from '@app/store';

/** Query param key for the URL to return to after identity validation. */
export const IDENTITY_RETURN_URL_QUERY_PARAM = 'returnUrl';

/**
 * Guard that allows access only when the active booking has at least one
 * guest verification with status VERIFIED. Use on routes that require
 * identity validation (e.g. access information page).
 * Redirects to /identity/validation with returnUrl so the user can be
 * sent back after successful verification.
 * If the booking has guestCanValidateIdentity === false and the user is not verified,
 * redirects to /error (user cannot validate for this booking).
 */
export const identityValidatedGuard: CanActivateFn = async (
  _route,
  state: RouterStateSnapshot,
) => {
  const bookingRepo = inject(BookingRepository);
  const router = inject(Router);
  const firebaseAuthenticationService = inject(FirebaseAuthenticationService);
  const bookingService = inject(BookingService);

  let user = await firebaseAuthenticationService.getCurrentUser();

  const booking = bookingRepo.getActiveBooking();

  const isGuestValidated = await firstValueFrom(
    bookingService.guestIsValidatedInBooking(
      booking?._id ?? '',
      user?.uid ?? '',
    ),
  );

  if (isGuestValidated) {
    return true;
  }

  if (booking?.guestCanValidateIdentity === false) {
    return router.createUrlTree(['/error']);
  }

  return router.createUrlTree(['/identity/validation'], {
    queryParams: {
      [IDENTITY_RETURN_URL_QUERY_PARAM]: state.url,
    },
  });
};
